import React, { useState } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import {
  BtnOutlineGreen,
  BtnOutlineRed,
  BtnRadiusRed,
  PNewItem,
} from '../../styled-components';
import { ReactComponent as IconPlus } from '../../images/icn-plus.svg';
import {
  Col,
  CustomInput,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import Autosuggest from '../../commons/Autosuggest';
import DateInput from '../../commons/DateInput';
import moment from 'moment';
import { PROFILE_TYPE_LABELS } from '../../lib/constants';

const INITIAL_SIGNAL_STATE = {
  signalman: {
    _id: '',
    businessName: '',
    type: '',
  },
  condo: {
    _id: '',
    name: '',
  },
  admin: {
    _id: '',
    businessName: '',
    type: '',
  },
  name: '',
  vatCode: '',
  street: '',
  postalCode: '',
  city: '',
  province: '',
  manufacturedUnitNumber: '',
  notes: '',

  reportingType: 'NOT_RECURRENT',
  signalType: '',
  estimate: 0,
  signalmanSharePerc: 0,
  extraPercentage: 0,

  commercial: {
    _id: '',
    name: '',
    fiscalCode: '',
  },
  commercialPerc: 0,
  commercial2: {
    _id: '',
    name: '',
    fiscalCode: '',
  },
  commercial2Perc: 0,
  commercial3: {
    _id: '',
    name: '',
    fiscalCode: '',
  },
  commercial3Perc: 0,

  file: '',

  accountabilityDate: null,
  managementStartDate: null,
  managementEndDate: null,
  balanceStartDate: null,
  balanceEndDate: null,
};

const ModalNewSignal = ({
  onConfirm,
  searchSignalmens,
  searchCondos,
  searchAdmins,
  checkVatCode,
  signalTypes,
}) => {
  const [t] = useTranslation('SIGNALS');
  const [modal, setModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const alert = useAlert();

  const [firstTry, setFirtsTry] = useState(true);

  const [formValues, setFormValues] = useState(INITIAL_SIGNAL_STATE);
  const [formErrors, setFormErrors] = useState({
    signalman: false,
    condo: false,
    admin: false,
    name: false,
    vatCode: false,
    street: false,
    postalCode: false,
    city: false,
    province: false,
    manufacturedUnitNumber: false,
    invalidManufacturedUnitNumber: false,

    signalType: false,
    estimate: false,
    signalmanSharePerc: false,
    extraPercentage: false,
    percentageMaxedOut: false,

    maxPercRecurrent: false,

    commercialPerc: false,
    commercialPerc2: false,
    commercialPerc3: false,

    file: false,

    accountabilityDate: false,
    balanceStartDate: false,
    balanceEndDate: false,
    managementStartDate: false,
    managementEndDate: false,
    managementStartDateBefore: false,
    managementEndDateAfter: false,
  });

  const toggle = () => {
    setModal(!modal);
    setFormValues({
      ...INITIAL_SIGNAL_STATE,
    });
    setFirtsTry(true);
  };

  function onInputChange({ target, uppercase }) {
    setFormValues((state) => ({
      ...state,
      [target.name]: uppercase ? target.value?.toUpperCase() : target.value,
    }));
  }

  const renderSignalmanSuggestion = ({ businessName = '', type = '' }) =>
    `${businessName} - ${PROFILE_TYPE_LABELS[type] || type}`;
  const getSignalmanSuggestionValue = ({ businessName = '' }) => businessName;
  function onSignalmanSuggestionSelect({ _id, businessName, type }) {
    setFormValues((state) => ({
      ...state,
      signalman: { _id, businessName, type },
    }));
  }

  function onCommercialSuggestionSelect({ _id, businessName, fiscalCode }) {
    setFormValues((state) => ({
      ...state,
      commercial: { _id, name: businessName, fiscalCode },
      commercialPerc: 0,
    }));
  }
  function onCommercial2SuggestionSelect({ _id, businessName, fiscalCode }) {
    setFormValues((state) => ({
      ...state,
      commercial2: { _id, name: businessName, fiscalCode },
      commercial2Perc: 0,
    }));
  }
  function onCommercial3SuggestionSelect({ _id, businessName, fiscalCode }) {
    setFormValues((state) => ({
      ...state,
      commercial3: { _id, name: businessName, fiscalCode },
      commercial3Perc: 0,
    }));
  }

  const renderAdminSuggestion = ({ businessName = '', type = '' }) =>
    `${businessName} - ${PROFILE_TYPE_LABELS[type] || type}`;
  const getAdminSuggestionValue = ({ businessName = '' }) => businessName;
  function onAdminSuggestionSelect({ _id, businessName, type }) {
    setFormValues((state) => ({
      ...state,
      admin: { _id, businessName, type },
    }));
  }

  const renderCondoSuggestion = ({ name = '' }) => name;
  const getCondoSuggestionValue = ({ name = '' }) => name;
  function onCondoSuggestionSelect({ _id, name }) {
    setFormValues((state) => ({
      ...state,
      condo: { _id, name },
    }));
  }

  const onNewSignalConfirm = async () => {
    const {
      signalman,
      condo,

      signalType,
      estimate,
      signalmanSharePerc,
      extraPercentage,

      commercial,
      commercialPerc,
      commercial2,
      commercial2Perc,
      commercial3,
      commercial3Perc,

      file,

      admin,
      accountabilityDate,
    } = formValues;

    const fileSize = (file.size / 1024 ** 2).toFixed(2);

    if (fileSize > 20) {
      alert.info('File troppo grande. Max 20Mb');
      return;
    }

    const errors = {};

    if (!signalman._id) errors.signalman = true;
    if (!signalType) errors.signalType = true;
    if (!estimate) errors.estimate = true;

    if (signalmanSharePerc !== 0 && !signalmanSharePerc)
      errors.signalmanSharePerc = true;
    if (extraPercentage !== 0 && !extraPercentage)
      errors.extraPercentage = true;

    if (commercial._id && !commercialPerc) errors.commercialPerc = true;
    if (commercial2._id && !commercial2Perc) errors.commercialPerc2 = true;
    if (commercial3._id && !commercial3Perc) errors.commercialPerc3 = true;

    if (signalmanSharePerc && signalmanSharePerc > 100)
      errors.percentageMaxedOut = true;
    if (extraPercentage && extraPercentage > 100)
      errors.percentageMaxedOut = true;
    if (
      Number(signalmanSharePerc || 0) +
        Number(extraPercentage || 0) +
        Number(commercialPerc || 0) +
        Number(commercial2Perc || 0) +
        Number(commercial3Perc || 0) >
      100
    )
      errors.percentageMaxedOut = true;

    if (!accountabilityDate) errors.accountabilityDate = true;
    if (!admin._id) errors.admin = true;

    if (!condo._id && signalType === 'CONDO') errors.condo = true;

    if (Object.keys(errors).length) {
      return setFormErrors(errors);
    }

    setFirtsTry(false);

    const data = new FormData();
    data.append('signalman', formValues.signalman._id);
    data.append('signalType', formValues.signalType);
    data.append('admin', formValues.admin._id);
    data.append(
      'signalmanSharePerc',
      Number(formValues.signalmanSharePerc || 0)
    );
    data.append('estimate', Number(formValues.estimate || 0));
    data.append('reportingType', formValues.reportingType);
    data.append('condoId', formValues.condo._id);
    data.append('notes', formValues.notes);
    data.append('extraPercentage', Number(formValues.extraPercentage || 0));
    data.append(
      'commercial',
      JSON.stringify({
        _id: commercial._id,
        name: commercial.name,
        fiscalCode: commercial.fiscalCode,
        perc: Number(commercialPerc || 0),
      })
    );
    data.append(
      'commercial2',
      JSON.stringify({
        _id: commercial2._id,
        name: commercial2.name,
        fiscalCode: commercial2.fiscalCode,
        perc: Number(commercial2Perc || 0),
      })
    );
    data.append(
      'commercial3',
      JSON.stringify({
        _id: commercial3._id,
        name: commercial3.name,
        fiscalCode: commercial3.fiscalCode,
        perc: Number(commercial3Perc || 0),
      })
    );
    data.append('accountabilityDate', formValues.accountabilityDate);
    if (formValues.file) data.append('file', formValues.file);

    try {
      onConfirm(data);
      toggle();
    } catch (error) {
      alert.error(error.message);
    }
  };

  const searchBeneficiaryWrapper = async (value) => {
    const signalType = formValues.signalType;

    return searchSignalmens({ value, signalType });
  };

  const searchSignalmensWrapper = async (value) => {
    const excluedIds = [];
    if (formValues.signalman._id) excluedIds.push(formValues.signalman._id);
    if (formValues.commercial._id) excluedIds.push(formValues.commercial._id);
    if (formValues.commercial2._id) excluedIds.push(formValues.commercial2._id);
    if (formValues.commercial3._id) excluedIds.push(formValues.commercial3._id);

    return searchSignalmens({ value, excludeIds: excluedIds });
  };

  function listenForEscapeEvent() {
    document.addEventListener('keydown', ({ key }) => {
      if (key === 'Escape') toggle();
    });
  }

  const {
    signalman,
    condo,

    signalType,
    estimate,
    signalmanSharePerc,
    extraPercentage,

    commercial,
    commercialPerc,
    commercial2,
    commercial2Perc,
    commercial3,
    commercial3Perc,

    admin,
    accountabilityDate,
    notes,
  } = formValues;

  return (
    <>
      <React.Fragment>
        <BtnRadiusRed onClick={toggle} type="button">
          <IconPlus />
        </BtnRadiusRed>
        <PNewItem
          onClick={toggle}
          style={{ marginLeft: '7px', marginRight: '0', cursor: 'pointer' }}
        >
          {t('NEW_SIGNAL')}
        </PNewItem>

        <Modal
          isOpen={modal}
          className="new-signal-modal"
          onOpened={listenForEscapeEvent}
        >
          <ModalHeader toggle={toggle}>{t('NEW_SIGNAL')}</ModalHeader>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              onNewSignalConfirm();
            }}
          >
            <ModalBody>
              <Row>
                <Col sm="12">
                  <Row>
                    <Col sm="12" md="6">
                      <FormGroup>
                        <Label>{t('SIGNAL_TYPES')} *</Label>
                        <CustomInput
                          type="select"
                          id="signalType"
                          name="signalType"
                          value={signalType}
                          defaultValue={signalType}
                          onChange={({ target: { value } }) => {
                            setFormValues({
                              ...formValues,
                              signalType: value,
                              admin: {
                                _id: '',
                                businessName: '',
                                type: '',
                              },
                              condo: { _id: '', name: '' },
                            });
                          }}
                        >
                          <option value={''}>Seleziona un tipo</option>
                          {signalTypes.map((signalType) => (
                            <option key={signalType._id} value={signalType._id}>
                              {signalType.name}
                            </option>
                          ))}
                        </CustomInput>
                        {formErrors.signalType && (
                          <FormText className="error-message">
                            {t('SIGNAL_TYPE_ERROR')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>
                    <Col xs="12" md="6">
                      <FormGroup>
                        <Label>{t('ACCOUNTABILITY_DATE')} *</Label>
                        <DateInput
                          name="accountabilityDate"
                          value={accountabilityDate}
                          onChange={(accountabilityDate) => {
                            setFormValues((state) => ({
                              ...state,
                              accountabilityDate:
                                moment(accountabilityDate).format(
                                  'YYYY-MM-DD'
                                ) + 'T00:00:00.000Z',
                            }));
                          }}
                        />
                        {formErrors.accountabilityDate && (
                          <FormText className="error-message">
                            {t('ACCOUNTABILITY_DATE_REQUIRED_ERROR')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs="12" md="6">
                      <FormGroup>
                        <Label>{t('SIGNALMAN')} *</Label>
                        <Autosuggest
                          placeholder="Cerca un segnalatore..."
                          onFetch={searchSignalmensWrapper}
                          onSelect={onSignalmanSuggestionSelect}
                          renderSuggestion={renderSignalmanSuggestion}
                          getSuggestionValue={getSignalmanSuggestionValue}
                          value={
                            signalman._id && signalman.type
                              ? `${signalman.businessName} - ${
                                  PROFILE_TYPE_LABELS[signalman.type] ||
                                  signalman.type
                                }`
                              : signalman.businessName
                          }
                          onChange={(businessName) =>
                            setFormValues((state) => ({
                              ...state,
                              signalman: {
                                _id: '',
                                type: '',
                                businessName,
                              },
                            }))
                          }
                        />

                        {formErrors.signalman && (
                          <FormText className="error-message">
                            {t('COMMON:REQUIRED')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>

                    <Col xs="12" md="6">
                      <FormGroup>
                        <Label>{t('ADMIN')} *</Label>
                        <Autosuggest
                          placeholder="Cerca un beneficiario..."
                          onFetch={searchBeneficiaryWrapper}
                          onSelect={onAdminSuggestionSelect}
                          renderSuggestion={renderAdminSuggestion}
                          getSuggestionValue={getAdminSuggestionValue}
                          disabled={formValues.signalType === ''}
                          value={
                            admin._id && admin.type
                              ? `${admin.businessName} - ${
                                  PROFILE_TYPE_LABELS[admin.type] || admin.type
                                }`
                              : admin.businessName
                          }
                          onChange={(businessName) => {
                            if (businessName === '') {
                              setFormValues((state) => ({
                                ...state,
                                admin: { _id: '', type: '', businessName: '' },
                              }));
                            } else {
                              setFormValues((state) => ({
                                ...state,
                                admin: {
                                  _id: '',
                                  type: '',
                                  businessName,
                                },
                              }));
                            }
                          }}
                        />

                        {formErrors.admin && (
                          <FormText className="error-message">
                            {t('COMMON:REQUIRED')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>

                    {signalTypes.find((st) => st._id === signalType)?.code ===
                      'CONDO' && (
                      <Col xs="12" md="6">
                        <FormGroup>
                          <Label>{t('CONDO')} *</Label>
                          <Autosuggest
                            placeholder="Cerca un condominio..."
                            onFetch={searchCondos}
                            onSelect={onCondoSuggestionSelect}
                            renderSuggestion={renderCondoSuggestion}
                            getSuggestionValue={getCondoSuggestionValue}
                            value={condo.name}
                            onChange={(name) =>
                              setFormValues((state) => ({
                                ...state,
                                condo: { ...condo, name },
                              }))
                            }
                          />

                          {formErrors.condo && (
                            <FormText className="error-message">
                              {t('COMMON:REQUIRED')}
                            </FormText>
                          )}
                        </FormGroup>
                      </Col>
                    )}
                  </Row>

                  <Row>
                    <Col xs="12" md="6">
                      <FormGroup>
                        <Label>{t('ESTIMATE')} *</Label>
                        <Input
                          type="number"
                          name="estimate"
                          value={estimate}
                          onChange={onInputChange}
                          min={0}
                          step="0.01"
                        />
                        {formErrors.estimate && (
                          <FormText className="error-message">
                            {t('COMMON:REQUIRED')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>

                    <Col xs="12" md="3">
                      <FormGroup>
                        <Label>{t('SIGNALMAN_PERCENTAGE')} *</Label>
                        <Input
                          type="number"
                          name="signalmanSharePerc"
                          value={signalmanSharePerc}
                          onChange={onInputChange}
                          min={0}
                          max={100}
                          step="0.01"
                        />
                        {formErrors.signalmanSharePerc && (
                          <FormText className="error-message">
                            {t('COMMON:REQUIRED')}
                          </FormText>
                        )}
                        {formErrors.percentageMaxedOut && (
                          <FormText className="error-message">
                            {t('PERCENTAGE_MAXED_OUT_ERROR')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>

                    <Col xs="12" md="3">
                      <FormGroup>
                        <Label>{t('EXTRA_PERCENTAGE')} *</Label>
                        <Input
                          type="number"
                          name="extraPercentage"
                          value={extraPercentage}
                          onChange={onInputChange}
                          min={0}
                          max={100}
                          step="0.01"
                        />
                        {formErrors.extraPercentage && (
                          <FormText className="error-message">
                            {t('COMMON:REQUIRED')}
                          </FormText>
                        )}
                        {formErrors.percentageMaxedOut && (
                          <FormText className="error-message">
                            {t('PERCENTAGE_MAXED_OUT_ERROR')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>

                    <Col xs="12">
                      <FormGroup>
                        <Label>{t('NOTES')}</Label>
                        <Input
                          type="text"
                          name="notes"
                          value={notes}
                          onChange={onInputChange}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col sm="12" md="6">
                      <FormGroup>
                        <Label>{t('AREA_MANAGER')}</Label>
                        <Autosuggest
                          placeholder="Cerca un responsabile di zona..."
                          onFetch={searchSignalmensWrapper}
                          onSelect={onCommercialSuggestionSelect}
                          renderSuggestion={renderSignalmanSuggestion}
                          getSuggestionValue={getSignalmanSuggestionValue}
                          value={commercial.name}
                          onChange={(name) => {
                            if (name === '') {
                              setFormValues((state) => ({
                                ...state,
                                commercial: {
                                  _id: '',
                                  name: '',
                                  fiscalCode: '',
                                },
                                commercialPerc: 0,
                              }));
                            } else {
                              setFormValues((state) => ({
                                ...state,
                                commercial: { ...commercial, name },
                              }));
                            }
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col xs="12" md="6">
                      <FormGroup>
                        <Label>{t('AREA_MANAGER_PERCENTAGE')} *</Label>
                        <Input
                          type="number"
                          name="commercialPerc"
                          value={commercialPerc}
                          onChange={onInputChange}
                          disabled={!commercial._id}
                          min={0}
                          max={100}
                          step="0.01"
                        />
                        {formErrors.commercialPerc && (
                          <FormText className="error-message">
                            {t('COMMON:REQUIRED')}
                          </FormText>
                        )}
                        {formErrors.percentageMaxedOut && (
                          <FormText className="error-message">
                            {t('PERCENTAGE_MAXED_OUT_ERROR')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col sm="12" md="6">
                      <FormGroup>
                        <Label>{t('COMMERCIAL')}</Label>
                        <Autosuggest
                          placeholder="Cerca un commerciale..."
                          onFetch={searchSignalmensWrapper}
                          onSelect={onCommercial2SuggestionSelect}
                          renderSuggestion={renderSignalmanSuggestion}
                          getSuggestionValue={getSignalmanSuggestionValue}
                          value={commercial2.name}
                          onChange={(name) => {
                            if (name === '') {
                              setFormValues((state) => ({
                                ...state,
                                commercial2: {
                                  _id: '',
                                  name: '',
                                  fiscalCode: '',
                                },
                                commercial2Perc: 0,
                              }));
                            } else {
                              setFormValues((state) => ({
                                ...state,
                                commercial2: { ...commercial2, name },
                              }));
                            }
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col xs="12" md="6">
                      <FormGroup>
                        <Label>{t('COMMERCIAL_PERCENTAGE')} *</Label>
                        <Input
                          type="number"
                          name="commercial2Perc"
                          value={commercial2Perc}
                          onChange={onInputChange}
                          disabled={!commercial2._id}
                          min={0}
                          max={100}
                          step="0.01"
                        />
                        {formErrors.commercialPerc2 && (
                          <FormText className="error-message">
                            {t('COMMON:REQUIRED')}
                          </FormText>
                        )}
                        {formErrors.percentageMaxedOut && (
                          <FormText className="error-message">
                            {t('PERCENTAGE_MAXED_OUT_ERROR')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col sm="12" md="6">
                      <FormGroup>
                        <Label>{t('COMMERCIAL')}</Label>
                        <Autosuggest
                          placeholder="Cerca un commerciale..."
                          onFetch={searchSignalmensWrapper}
                          onSelect={onCommercial3SuggestionSelect}
                          renderSuggestion={renderSignalmanSuggestion}
                          getSuggestionValue={getSignalmanSuggestionValue}
                          value={commercial3.name}
                          onChange={(name) => {
                            if (name === '') {
                              setFormValues((state) => ({
                                ...state,
                                commercial3: {
                                  _id: '',
                                  name: '',
                                  fiscalCode: '',
                                },
                                commercial3Perc: 0,
                              }));
                            } else {
                              setFormValues((state) => ({
                                ...state,
                                commercial3: { ...commercial3, name },
                              }));
                            }
                          }}
                        />
                      </FormGroup>
                    </Col>
                    <Col xs="12" md="6">
                      <FormGroup>
                        <Label>{t('COMMERCIAL_PERCENTAGE')} *</Label>
                        <Input
                          type="number"
                          name="commercial3Perc"
                          value={commercial3Perc}
                          onChange={onInputChange}
                          disabled={!commercial3._id}
                          min={0}
                          max={100}
                          step="0.01"
                        />
                        {formErrors.commercialPerc3 && (
                          <FormText className="error-message">
                            {t('COMMON:REQUIRED')}
                          </FormText>
                        )}
                        {formErrors.percentageMaxedOut && (
                          <FormText className="error-message">
                            {t('PERCENTAGE_MAXED_OUT_ERROR')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm="12">
                      <FormGroup>
                        <Label for="selectFile">
                          {t('FILES:CHOOSE_FILE')}
                          &nbsp;({t('FILES:MAX_UPLOAD_FILE_SIZE')})
                        </Label>
                        <CustomInput
                          type="file"
                          id="attachedFile"
                          name="attachedFile"
                          accept="application/pdf"
                          onChange={(event) =>
                            setFormValues({
                              ...formValues,
                              file: event.target.files[0],
                            })
                          }
                        />
                        {formValues.file === '' && !firstTry && (
                          <FormText className="error-message">
                            {t('FILE_ERROR')}
                          </FormText>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <BtnOutlineRed
                type="button"
                className="uppercase"
                onClick={toggle}
              >
                {t('COMMON:CANCEL')}
              </BtnOutlineRed>
              <BtnOutlineGreen type="submit" className="uppercase">
                {t('COMMON:CONFIRM')}
              </BtnOutlineGreen>
            </ModalFooter>
          </Form>
        </Modal>
      </React.Fragment>
    </>
  );
};

export default ModalNewSignal;
